import axios from "axios";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { auth } from "../firebase/firebase";

const linguistApi = axios.create({
  baseURL: `${process.env.REACT_APP_BASE_URL || "http://localhost:5001"}/linguistPortal`,
  withCredentials: true,
});

const isPublicLinguistEndpoint = (url = "") => {
  const path = String(url || "").toLowerCase();
  return (
    path.includes("/forgot-password") ||
    path.includes("/setup-password") ||
    path.includes("/login-history") ||
    path.includes("/notify-password-changed")
  );
};

export const waitForLinguistAuthUser = (timeoutMs = 6000) =>
  new Promise((resolve) => {
    if (auth.currentUser) {
      resolve(auth.currentUser);
      return;
    }

    let resolved = false;
    let unsubscribe = null;

    const finalize = (user) => {
      if (resolved) return;
      resolved = true;
      if (unsubscribe) unsubscribe();
      resolve(user || null);
    };

    unsubscribe = onAuthStateChanged(auth, (user) => {
      finalize(user);
    });

    setTimeout(() => {
      finalize(auth.currentUser || null);
    }, timeoutMs);
  });

linguistApi.interceptors.request.use(
  async (config) => {
    if (isPublicLinguistEndpoint(config?.url)) {
      return config;
    }

    const currentUser = auth.currentUser || (await waitForLinguistAuthUser());

    if (currentUser) {
      try {
        let token = "";

        try {
          token = await currentUser.getIdToken();
        } catch (tokenError) {
          // Force refresh as fallback when cached token retrieval fails.
          token = await currentUser.getIdToken(true);
          console.warn("Linguist API: fallback token refresh succeeded after getIdToken failure.", tokenError);
        }

        config.headers.Authorization = `Bearer ${token}`;
      } catch (error) {
        const requestError = new Error("Linguist auth token unavailable");
        requestError.code = "LINGUIST_AUTH_TOKEN_UNAVAILABLE";
        requestError.cause = error;
        return Promise.reject(requestError);
      }

      return config;
    }

    const authStateError = new Error("Linguist auth user unavailable");
    authStateError.code = "LINGUIST_AUTH_USER_UNAVAILABLE";
    if (typeof window !== "undefined") {
      console.warn("Linguist API: no Firebase user during protected request", {
        url: config?.url,
        path: window.location.pathname,
      });
    }
    return Promise.reject(authStateError);
  },
  (error) => Promise.reject(error),
);

linguistApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401 && error.config) {
      const retryCount = Number(error.config._linguistAuthRetryCount || 0);

      if (retryCount < 2) {
        const user = auth.currentUser || (await waitForLinguistAuthUser());

        if (user) {
          try {
            const refreshedToken = await user.getIdToken(true);
            error.config._linguistAuthRetryCount = retryCount + 1;
            error.config.headers = error.config.headers || {};
            error.config.headers.Authorization = `Bearer ${refreshedToken}`;
            return linguistApi(error.config);
          } catch (refreshError) {
            console.warn("Linguist API: token refresh failed after 401.", refreshError);
          }
        }
      }
    }

    if (error.response?.status === 403 && error.response?.data?.accountDisabled) {
      if (auth.currentUser) {
        try {
          await signOut(auth);
        } catch (signOutError) {
          console.warn("Could not sign out disabled linguist account:", signOutError);
        }
      }
    }

    return Promise.reject(error);
  },
);

export default linguistApi;